import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Checkbox,
  Menu,
  Typography,
  Divider,
  CircularProgress,
  TextField,
  InputAdornment,
} from "@mui/material";
import { 
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { observer } from "mobx-react-lite";
import { store } from "../../../app/stores/store";
import { SubCategory } from "../../../app/stores/productStore";

interface SubcategoryFilterProps {
  selectedCategories: number[];
  selectedSubCategories: string[];
  onSubCategoryChange: (subCategoryNames: string[]) => void;
  onApplyFilter: () => void;
  onClearFilters?: (clearType: 'all' | 'subcategories') => void;
}

const SubcategoryFilter: React.FC<SubcategoryFilterProps> = observer(({
  selectedCategories,
  selectedSubCategories,
  onSubCategoryChange,
  onApplyFilter,
  onClearFilters,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const open = Boolean(anchorEl);

  const { subCategories } = store.productStore;

  // Filter subcategories to only show those belonging to selected categories
  const availableSubCategories = subCategories.filter((sub) =>
    selectedCategories.includes(sub.categoryId || 0)
  );

  // Further filter by search query
  const filteredSubCategories = availableSubCategories.filter((sub) =>
    sub.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load subcategories when selected categories change
  useEffect(() => {
    const loadSubcategories = async () => {
      if (selectedCategories.length > 0) {
        setLoading(true);
        await Promise.all(
          selectedCategories.map((catId) =>
            store.productStore.loadSubCategoriesForCategory(catId)
          )
        );
        setLoading(false);
      }
    };

    loadSubcategories();
  }, [selectedCategories]);

  // Remove subcategories from selection when their parent category is deselected
  useEffect(() => {
    const validSubCategories = selectedSubCategories.filter((subName) => {
      const sub = subCategories.find((s) => s.name === subName);
      return sub && selectedCategories.includes(sub.categoryId || 0);
    });

    if (validSubCategories.length !== selectedSubCategories.length) {
      onSubCategoryChange(validSubCategories);
    }
  }, [selectedCategories, subCategories, selectedSubCategories, onSubCategoryChange]);

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSearchQuery("");
  };

  const handleSubCategoryToggle = (subCategoryName: string) => {
    if (selectedSubCategories.includes(subCategoryName)) {
      onSubCategoryChange(selectedSubCategories.filter((name) => name !== subCategoryName));
    } else {
      onSubCategoryChange([...selectedSubCategories, subCategoryName]);
    }
  };

  const clearAll = () => {
    if (onClearFilters) {
      onClearFilters('subcategories');
    } else {
      onSubCategoryChange([]);
    }
  };

  const applyFilter = () => {
    onApplyFilter();
    handleClose();
  };

  return (
    <Box sx={{ mb: { xs: 1, md: 0 }, p: 0 }}>
      <Button
        variant="outlined"
        onClick={handleClick}
        endIcon={<KeyboardArrowDownIcon />}
        disabled={selectedCategories.length === 0}
        sx={{
          textTransform: "none",
          color: "#000",
          borderColor: "#e0e0e0",
          backgroundColor: "#fff",
          borderRadius: 1,
          px: 2,
          py: 1,
          "&:hover": {
            backgroundColor: "#f5f5f5",
            borderColor: "#e0e0e0",
          },
          "&:disabled": {
            color: "#bdbdbd",
            borderColor: "#e0e0e0",
          },
        }}
      >
        Subcategories {selectedSubCategories.length > 0 && `(${selectedSubCategories.length})`}
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            minWidth: 280,
            mt: 1,
            borderRadius: 2,
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          },
        }}
        MenuListProps={{
          sx: {
            padding: 0,
          },
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1,
          }}
        >
          <Typography variant="subtitle1" fontWeight={600}>
            Subcategories
          </Typography>
          {selectedSubCategories.length > 0 && (
            <Button
              size="small"
              onClick={clearAll}
              sx={{
                textTransform: "none",
                color: "#1976d2",
                fontWeight: 500,
                minWidth: "auto",
                p: 0.5,
                "&:hover": {
                  backgroundColor: "transparent",
                  textDecoration: "underline",
                },
              }}
            >
              Clear All
            </Button>
          )}
        </Box>

        <Divider />

        {/* Search Input */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search subcategories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>

        <Divider />

        {/* Subcategory List*/}
        <Box sx={{ maxHeight: 300, overflowY: "auto" }}>
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                py: 4,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : filteredSubCategories.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ px: 2, py: 2, textAlign: "center" }}
            >
              {searchQuery 
                ? "No subcategories found" 
                : selectedCategories.length === 0
                ? "Please select a category first"
                : "No subcategories available"}
            </Typography>
          ) : (
            filteredSubCategories.map((subCategory: SubCategory) => (
              <Box
                key={subCategory.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                  },
                }}
                onClick={() => handleSubCategoryToggle(subCategory.name)}
              >
                <Typography variant="body2">{subCategory.name}</Typography>
                <Checkbox
                  size="small"
                  checked={selectedSubCategories.includes(subCategory.name)}
                  onChange={() => handleSubCategoryToggle(subCategory.name)}
                  onClick={(e) => e.stopPropagation()}
                />
              </Box>
            ))
          )}
        </Box>

        <Divider />

        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={applyFilter}
            disabled={selectedSubCategories.length === 0}
            sx={{
              textTransform: "none",
              backgroundColor: "#1976d2",
              "&:hover": {
                backgroundColor: "#1565c0",
              },
            }}
          >
            Apply Filter
          </Button>
        </Box>
      </Menu>
    </Box>
  );
});

export default SubcategoryFilter;
